import { match } from 'ts-pattern'
import i18next from 'i18next'
import type { UserMutator } from '@/lib/entity/user'
import type { Converter } from '@/lib/converter/entityFormatter'
import { asModifiedField } from '@/lib/converter/entityFormatter'
import { sql } from 'kysely'
import { withStage } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'

type UserSchema = UserMutator['merged']

export const UserConverter: Converter<UserSchema> = {
  icon: () => i18next.t('stages.icon.user'),
  title: () => i18next.t('stages.table.user'),
  subTitle: async (merged) => merged.name ?? i18next.t('ui.common.inputting'),
  modifiedFields: (modified, column) => {
    const label = i18next.t(`stages.labels.user.${column}`)
    return match(column)
      .with('departmentIds', async () => {
        const departmentIds = removeUndefinedFromArray(modified.departmentIds!)
        const departmentNames = await getConcatenatedNames('department', departmentIds)
        return asModifiedField(label, departmentNames)
      })
      .with('jobTitleIds', async () => {
        const jobTitleIds = removeUndefinedFromArray(modified.jobTitleIds!)
        const jobTitleNames = await getConcatenatedNames('job_title', jobTitleIds)
        return asModifiedField(label, jobTitleNames)
      })
      .with('jobSkillIds', async () => {
        const jobSkillIds = removeUndefinedFromArray(modified.jobSkillIds!)
        const jobSkillNames = await getConcatenatedNames('job_skill', jobSkillIds)
        return asModifiedField(label, jobSkillNames)
      })
      .with('note', () => asModifiedField(label, truncateString(modified.note!)))
      .with('display_order', () => asModifiedField(label, modified.display_order!.toString()))
      .with('name', 'email', 'role', 'available', (column) => {
        if (column === 'role') {
          return asModifiedField(label, i18next.t(`role.${modified[column]}`))
        }
        return asModifiedField(label, modified[column]!)
      })
      .with('id', 'firebase_uid', () => undefined)
      .exhaustive()
  }
}

const getConcatenatedNames = async (
  tableName: 'department' | 'job_title' | 'job_skill',
  ids: string[]
): Promise<string> => {
  const result = await withStage(async (db: Kysely<DB>) => {
    return (
      (await db
        .selectFrom(tableName)
        .select([sql<string>`IFNULL(group_concat(name),'')`.as('names')])
        .where('id', 'in', ids)
        .executeTakeFirst()) || { names: '' }
    )
  })
  return truncateString(result.names)
}

const truncateString = (str: string, length = 15): string =>
  str.length > length ? str.substring(0, length) + '...' : str

const removeUndefinedFromArray = (ids: (string | undefined)[]): string[] =>
  ids.filter((item): item is string => item !== undefined)
